/**
 * imageOptimizer.js
 * Automatic client-side WebP converter and compressor.
 * Runs in the browser before image upload, guaranteeing:
 *  1. Format is converted to .webp automatically.
 *  2. High-res DSLR/phone photos (10MB-20MB) are resized and compressed to ~150KB-300KB.
 *  3. Bypasses PHP upload_max_filesize limits on cPanel servers.
 *  4. Drastically improves upload speed and user experience.
 */

export const convertImageToWebP = (file, maxWidth = 1600, quality = 0.82) => {
  return new Promise((resolve) => {
    if (!file || !(file instanceof File || file instanceof Blob)) {
      resolve({ file, previewUrl: null, origSize: 0, newSize: 0, reduction: 0 });
      return;
    }

    const origSize = file.size;
    const origName = file.name || 'upload.jpg';
    const baseName = origName.substring(0, origName.lastIndexOf('.')) || origName;
    const newFileName = `${baseName}.webp`;

    // If browser doesn't support FileReader or Canvas, return original file
    if (typeof window === 'undefined' || !window.FileReader || !window.HTMLCanvasElement) {
      resolve({ file, previewUrl: URL.createObjectURL(file), origSize, newSize: origSize, reduction: 0 });
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let width = img.width;
        let height = img.height;

        // Scale proportionally if exceeds maxWidth
        if (width > maxWidth || height > maxWidth) {
          if (width > height) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          } else {
            width = Math.round((width * maxWidth) / height);
            height = maxWidth;
          }
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          resolve({ file, previewUrl: URL.createObjectURL(file), origSize, newSize: origSize, reduction: 0 });
          return;
        }

        // Draw image onto canvas
        ctx.drawImage(img, 0, 0, width, height);

        // Convert to WebP blob
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              resolve({ file, previewUrl: URL.createObjectURL(file), origSize, newSize: origSize, reduction: 0 });
              return;
            }

            const webpFile = new File([blob], newFileName, {
              type: 'image/webp',
              lastModified: Date.now()
            });

            const newSize = webpFile.size;
            const reduction = origSize > 0 ? Math.round(((origSize - newSize) / origSize) * 100) : 0;
            const previewUrl = URL.createObjectURL(webpFile);

            resolve({
              file: webpFile,
              previewUrl,
              origSize,
              newSize,
              reduction: reduction > 0 ? reduction : 0,
              width,
              height
            });
          },
          'image/webp',
          quality
        );
      };

      img.onerror = () => {
        resolve({ file, previewUrl: URL.createObjectURL(file), origSize, newSize: origSize, reduction: 0 });
      };

      img.src = e.target.result;
    };

    reader.onerror = () => {
      resolve({ file, previewUrl: URL.createObjectURL(file), origSize, newSize: origSize, reduction: 0 });
    };

    reader.readAsDataURL(file);
  });
};

export const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

export default convertImageToWebP;
